import Axios, { AxiosInstance } from "axios";
import { plainToClass } from "class-transformer";
import { singleton } from "tsyringe";
import { AdvisorInfo, GenerateOTPRequest, GenerateOTPResponse, SignUpResponse, UserSignUP } from "../App/WebStory/Models/WebStoryModel";
import { ClientSignupRequest, GenerateSignupOTPRequest } from "../App/ClientOnboarding/models";

export const SERVER_URL = import.meta.env.REACT_APP_SERVER_URL ?? "";
export const CORE_SERVER_URL = import.meta.env.REACT_APP_CORE_SERVER_URL ?? "";

@singleton()
export default class PartnerApiServices {
    instance: AxiosInstance = Axios.create({
        baseURL: SERVER_URL
    });

    coreInstance: AxiosInstance = Axios.create({
        baseURL: CORE_SERVER_URL
    });

    constructor() {
        this.instance.interceptors.request.use((config) => {
            return config;
        }, (error => {
            return Promise.reject(error);
        }));

        this.instance.interceptors.response.use((v) => v, (error) => {
            if (error?.response?.data?.userMsg)
                error.message = error.response.data.userMsg;
            return Promise.reject(error);
        });

        this.coreInstance.interceptors.response.use((v) => v, (error) => {
            if (error?.response?.data?.userMsg)
                error.message = error.response.data.userMsg;
            return Promise.reject(error);
        });
    }

    async getAdvisorInfo(advisorId: string): Promise<AdvisorInfo> {
        const responseFromGetAdvisorInfo = await this.instance.request({
            method: "POST",
            url: "/auth/getAdvisorInfo",
            data: {
                advisorId,
            },
        });
        return plainToClass(AdvisorInfo, responseFromGetAdvisorInfo.data);
    }


    async generateOTP(data: GenerateOTPRequest): Promise<GenerateOTPResponse> {
        const response = await this.instance.request({
            method: "POST",
            url: "/auth/generateSignupOTP",
            data
        });
        return plainToClass(GenerateOTPResponse, response.data);
    }

    async verifyOTP(data: UserSignUP): Promise<SignUpResponse> {
        const response = await this.instance.request({
            method: "POST",
            url: "/auth/signup",
            data
        });

        return plainToClass(SignUpResponse, response.data);
    }

    // --- Client Onboarding (core-server) ---

    async getClientOnboardingAdvisorInfo(advisorId: string): Promise<AdvisorInfo> {
        const response = await this.coreInstance.request({
            method: "POST",
            url: "/mweb/clientOnboarding/getAdvisorInfo",
            data: { advisorId },
        });
        return plainToClass(AdvisorInfo, response.data);
    }

    async generateSignupOTP(data: GenerateSignupOTPRequest): Promise<GenerateOTPResponse> {
        const response = await this.coreInstance.request({
            method: "POST",
            url: "/mweb/clientOnboarding/generateSignupOTP",
            data
        });
        return plainToClass(GenerateOTPResponse, response.data);
    }

    async clientSignup(data: ClientSignupRequest): Promise<SignUpResponse> {
        const response = await this.coreInstance.request({
            method: "POST",
            url: "/mweb/clientOnboarding/signup",
            data
        });
        return plainToClass(SignUpResponse, response.data);
    }

    // Partner-initiated approval: validate OTP + delegate to appserver approve.js
    // (via core-server). Returns the app-download link from approve.js.
    async approveClientSignup(tkn: string, otp: string): Promise<{ link: string }> {
        const response = await this.coreInstance.request({
            method: "POST",
            url: "/mweb/clientOnboarding/approve",
            data: { tkn, otp },
        });
        return response.data;
    }

    // NPS score from the fallback email. `alreadyRecorded` means the cycle was answered
    // elsewhere first — not an error.
    // expired: the cycle's 90-day window closed and a new one opened, so this link is stale
    // and nothing was recorded — distinct from alreadyRecorded, where the score is safely in.
    async submitNpsEmailScore(tkn: string, score: number): Promise<{ ok: boolean; alreadyRecorded?: boolean; expired?: boolean }> {
        const response = await this.coreInstance.request({
            method: "POST",
            url: "/mweb/npsFeedback/submit",
            data: { tkn, score },
        });
        return response.data;
    }

    async checkAvailability(data: { phoneNumber?: string; email?: string }): Promise<void> {
        await this.coreInstance.request({
            method: "POST",
            url: "/mweb/clientOnboarding/checkAvailability",
            data
        });
    }

    async processSource(token: string, productSource: string): Promise<void> {
        await this.coreInstance.request({
            method: "POST",
            url: "/mweb/clientSignup/processSource",
            data: { token, productSource },
        });
    }

    async uploadMandate(mandate: FormData, token: string): Promise<void> {
        await this.instance.request({
            method: "POST",
            url: "/mweb/mandate/uploadXSIP",
            data: mandate,
            headers: {
                Authorization: "Bearer " + token
            }
        });
    }

}
